import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, Star, Filter, RefreshCw, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FamiliiRecenzii — tab „Familii" în pagina Apeluri Recenzii.
 *
 * DESIGN identic cu pagina Recenzii → Recenzii pe produs, DAR toate datele
 * afișate provin STRICT din colecțiile Apeluri Recenzii (nu din Sheets/Reviews):
 *
 *   • Header (nume, SKU-uri, link eMAG) — copiat din `review_product_groups`
 *   • Steluta dreapta-sus = `confirmed_total` (dovezi Recenzii Confirmate).
 *     Click pe steluta → popup cu lista completă.
 *   • Rată conversie = confirmed_total / total_answered * 100
 *   • Rată reală    = confirmed_total / total_non_asteptare * 100
 *   • De sunat      = telefoane cu status implicit (in_asteptare)
 *   • Recenzii 14z / 30z = dovezi confirmate în interval
 *   • Buc. 30z       = bucăți vândute FBE RO în ultimele 30 zile
 *   • Recenzii eMAG total + distribuție stele = copiate direct
 *
 * NU se afișează (per user cerință): Cost/rec, Profit mediu, Profit după recenzii,
 * Următoarele recenzii, Contacte de adăugat, Cost/rec estimat, Ultima comandă.
 *
 * Buton „Vezi comenzi în așteptare" → comută la tab-ul Comenzi, filtrat pe
 * TOATE SKU-urile familiei (comma-separated), status = În Așteptare Apel.
 */
export default function FamiliiRecenzii({ onNavigateToComenzi }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [popupFamily, setPopupFamily] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/apeluri-recenzii/families`);
      setGroups(r.data?.items || []);
    } catch (e) {
      toast.error("Nu am putut încărca familiile", {
        description: e.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totalConfirmed = groups.reduce((s, g) => s + (g.confirmed_total || 0), 0);

  return (
    <div className="space-y-4" data-testid="familii-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-zinc-100">Familii de produse</h2>
          <p className="text-[11px] text-zinc-500">
            {groups.length} familii · <span className="font-mono text-amber-300">{totalConfirmed}</span> recenzii confirmate (AR)
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="familii-reload"
          className="inline-flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1 text-xs text-zinc-200 hover:border-zinc-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Reîncarcă
        </button>
      </div>

      {loading && groups.length === 0 && (
        <p className="py-8 text-center text-sm text-zinc-500">Se încarcă…</p>
      )}
      {!loading && groups.length === 0 && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-8 text-center">
          <Star className="mx-auto h-8 w-8 text-zinc-600" />
          <p className="mt-2 text-sm text-zinc-400">Nicio familie de produse configurată.</p>
          <p className="mt-1 text-xs text-zinc-500">Adaugă familii din pagina „Recenzii".</p>
        </div>
      )}

      {groups.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => (
            <FamilyCard
              key={g.id}
              g={g}
              onNavigateToComenzi={onNavigateToComenzi}
              onOpenConfirmations={() => setPopupFamily(g)}
            />
          ))}
        </div>
      )}

      {popupFamily && (
        <ConfirmationsPopup family={popupFamily} onClose={() => setPopupFamily(null)} />
      )}
    </div>
  );
}

function FamilyCard({ g, onNavigateToComenzi, onOpenConfirmations }) {
  const conv = Number(g.conversion_rate || 0) * 100;
  const real = Number(g.real_conversion_rate || 0) * 100;
  const pnks = Array.isArray(g.pnks) ? g.pnks.filter(Boolean) : [];
  const primaryPnk = pnks[0];
  const emagHref = primaryPnk ? `https://www.emag.ro/-/pd/${primaryPnk}` : null;

  return (
    <div
      data-testid={`familie-card-${g.id}`}
      className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4 transition hover:border-orange-500/40"
    >
      {/* 1. Header — nume + link eMAG + count confirmate (click pt popup) */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold leading-tight text-zinc-100">{g.name}</h3>
        <div className="flex flex-shrink-0 items-center gap-1.5">
          {emagHref && (
            <a
              href={emagHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`familie-emag-${g.id}`}
              title={pnks.length > 1 ? `${pnks.length} produse pe eMAG` : `Deschide pe eMAG`}
              className="inline-flex items-center gap-1 rounded-md border border-orange-500/40 bg-orange-500/10 px-1.5 py-0.5 text-[10px] font-medium text-orange-200 hover:bg-orange-500/20"
            >
              <ExternalLink className="h-3 w-3" /> eMAG
            </a>
          )}
          <button
            type="button"
            onClick={onOpenConfirmations}
            data-testid={`familie-confirmed-badge-${g.id}`}
            title="Vezi lista recenziilor confirmate pentru familie"
            className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs font-bold text-amber-200 transition hover:bg-amber-500/20"
          >
            <Star className="h-3 w-3" />
            {g.confirmed_total || 0}
          </button>
        </div>
      </div>

      {/* SKU pills */}
      <div className="mt-2 flex flex-wrap gap-1">
        {(g.skus || []).map((s) => (
          <span key={s} className="rounded-md bg-zinc-800 px-1.5 py-0.5 font-mono text-[10px] text-zinc-300">
            {s}
          </span>
        ))}
      </div>

      {/* 2-4. Rate + De sunat */}
      <div className="mt-3 grid grid-cols-3 gap-1.5">
        <div
          className="rounded-md border border-sky-500/30 bg-sky-500/5 px-2 py-1.5"
          title={`${g.confirmed_total || 0} recenzii / ${g.total_answered || 0} au răspuns`}
        >
          <div className="text-[9px] uppercase tracking-wider text-sky-300/80">Rată conversie</div>
          <div className="font-mono text-sm font-bold text-sky-300">{conv.toFixed(1)}%</div>
          <div className="text-[9px] text-zinc-500">{g.confirmed_total || 0} / {g.total_answered || 0}</div>
        </div>
        <div
          className="rounded-md border border-amber-500/30 bg-amber-500/5 px-2 py-1.5"
          title={`Rată REALĂ: ${g.confirmed_total || 0} recenzii / ${g.total_non_asteptare || 0} contactați (status setat manual)`}
        >
          <div className="text-[9px] uppercase tracking-wider text-amber-300/80">Rată reală</div>
          <div className="font-mono text-sm font-bold text-amber-300">{real.toFixed(1)}%</div>
          <div className="text-[9px] text-zinc-500">{g.confirmed_total || 0} / {g.total_non_asteptare || 0}</div>
        </div>
        <div
          className="rounded-md border border-violet-500/30 bg-violet-500/5 px-2 py-1.5"
          title="Clienți în așteptare apel pentru SKU-urile familiei"
        >
          <div className="text-[9px] uppercase tracking-wider text-violet-300/80">De sunat</div>
          <div className="font-mono text-sm font-bold text-violet-200">{g.de_sunat || 0}</div>
          <div className="text-[9px] text-zinc-500">rânduri In Așteptare</div>
        </div>
      </div>

      {/* 5-7. Recenzii 14z / Recenzii 30z / Buc 30z (fără Cost/rec, per user) */}
      <div className="mt-2 grid grid-cols-3 gap-1.5">
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-1.5 py-1.5" title="Dovezi confirmate în ultimele 14 zile (AR)">
          <div className="text-[9px] uppercase text-emerald-300/80">Recenzii 14z</div>
          <div className="font-mono text-sm font-bold text-emerald-300">{g.confirmed_14d || 0}</div>
        </div>
        <div className="rounded-md border border-teal-500/30 bg-teal-500/5 px-1.5 py-1.5" title="Dovezi confirmate în ultimele 30 zile (AR)">
          <div className="text-[9px] uppercase text-teal-300/80">Recenzii 30z</div>
          <div className="font-mono text-sm font-bold text-teal-300">{g.confirmed_30d || 0}</div>
        </div>
        <div className="rounded-md border border-zinc-800 bg-zinc-950/70 px-1.5 py-1.5" title="Bucăți vândute FBE RO în ultimele 30 zile (comenzi finalizate)">
          <div className="text-[9px] uppercase text-zinc-400">Buc. 30z</div>
          <div className="font-mono text-sm font-bold text-zinc-100">{g.units_sold_last_30_days || 0}</div>
        </div>
      </div>

      {/* Buton „Vezi comenzi în așteptare" — filtrează tab-ul Comenzi pe TOATE SKU-urile familiei */}
      <button
        type="button"
        onClick={() => onNavigateToComenzi?.(g.skus || [], g.name)}
        data-testid={`familie-goto-comenzi-${g.id}`}
        className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-orange-500/40 bg-orange-500/10 px-2 py-1.5 text-[11px] font-medium text-orange-100 hover:bg-orange-500/20"
        title={`Deschide comenzile În Așteptare pentru SKU-urile din familia „${g.name}"`}
      >
        <Filter className="h-3 w-3" />
        Vezi comenzi în așteptare
      </button>

      {/* Recenzii eMAG (total) + distribuție stele — copiat direct din DB */}
      {g.emag_total_reviews != null && (
        <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase text-amber-300/80">Recenzii eMAG (total)</span>
            <span className="font-mono text-sm font-bold text-amber-200">{g.emag_total_reviews}</span>
          </div>
          <div className="mt-1 grid grid-cols-5 gap-1">
            {[5, 4, 3, 2, 1].map((s) => {
              const n = Number((g.emag_rating_distribution || {})[String(s)] || 0);
              const isPos = s >= 4;
              return (
                <div
                  key={s}
                  data-testid={`familie-${g.id}-star-${s}`}
                  title={`${s} stele: ${n} recenzii`}
                  className={`rounded border px-1 py-0.5 text-center ${
                    isPos ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"
                  }`}
                >
                  <div className={`text-[9px] ${isPos ? "text-emerald-300/80" : "text-red-300/80"}`}>{s}★</div>
                  <div className={`font-mono text-[11px] font-bold ${isPos ? "text-emerald-200" : "text-red-200"}`}>{n}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ConfirmationsPopup — modal cu lista completă a recenziilor confirmate
 * (dovezi) pentru o familie. Deschis la click pe steluta din card.
 */
function ConfirmationsPopup({ family, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/apeluri-recenzii/families/${family.id}/confirmed`);
        if (!ignore) setItems(r.data?.items || []);
      } catch (e) {
        if (!ignore) toast.error("Nu am putut încărca confirmările", { description: e.response?.data?.detail || e.message });
      } finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, [family.id]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="familie-confirmed-popup"
    >
      <div
        className="relative max-h-[85vh] w-full max-w-2xl overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 border-b border-zinc-800 bg-zinc-900/60 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-zinc-100">Recenzii confirmate — {family.name}</h3>
            <p className="mt-0.5 text-[11px] text-zinc-500">
              {items.length} dovezi (Apeluri Recenzii → Recenzii Confirmate)
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="familie-confirmed-popup-close"
            className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-3">
          {loading && <p className="py-6 text-center text-sm text-zinc-500">Se încarcă…</p>}
          {!loading && items.length === 0 && (
            <p className="py-6 text-center text-sm text-zinc-500">Nicio recenzie confirmată pentru această familie.</p>
          )}
          {!loading && items.length > 0 && (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-zinc-500">
                <tr>
                  <th className="px-2 py-1.5 text-left">Client</th>
                  <th className="px-2 py-1.5 text-left">Telefon</th>
                  <th className="px-2 py-1.5 text-left">Nr. comandă</th>
                  <th className="px-2 py-1.5 text-left">Data</th>
                  <th className="px-2 py-1.5 text-left">Dovadă</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.proof_id || `${it.phone}-${it.uploaded_at}`} className="border-t border-zinc-800/60">
                    <td className="px-2 py-1.5 text-zinc-200">{it.customer_name || "—"}</td>
                    <td className="px-2 py-1.5 font-mono text-zinc-300">{it.phone}</td>
                    <td className="px-2 py-1.5 font-mono text-orange-200">{it.order_id || "—"}</td>
                    <td className="px-2 py-1.5 text-zinc-400">
                      {it.uploaded_at ? String(it.uploaded_at).slice(0, 10) : "—"}
                    </td>
                    <td className="px-2 py-1.5">
                      {it.url ? (
                        <a
                          href={it.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-medium text-sky-200 hover:bg-sky-500/20"
                        >
                          <ExternalLink className="h-3 w-3" /> Vezi
                        </a>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
